import type { Object3D, SceneControl } from '@anov/3d-core'
import { Color, Layers, MeshBasicMaterial, ShaderMaterial, SpriteMaterial, Vector2, lib, use } from '@anov/3d-core'

const { EffectComposer, RenderPass, UnrealBloomPass, ShaderPass, GammaCorrectionShader, CopyShader, FXAAShader } = lib

type HighParams = {
  strength?: number
  radius?: number
  threshold?: number
}

/**
 * @file createBloomSelectedTool
 * todo 待优化
 */

/**
 * createBloomSelectedTool
 * @param highParams
 * @returns
 */
export const createBloomSelectedTool = (highParams: HighParams = {}, sceneControl: SceneControl) => {
  const { scene, camera, renderer } = use.useScene(sceneControl)
  const { strength = 1.5, radius = 0.4, threshold = 0.85 } = highParams || {}

  const composer = sceneControl.context.storeManagement.get('composer') as lib.EffectComposer

  if (!scene || !camera || !renderer)
    throw new Error('scene or camera is not defined,')

  // @ts-ignore
  composer.isNeedRender = false

  const BLOOM_SCENE = 1
  const bloomLayer = new Layers()

  bloomLayer.set(BLOOM_SCENE)

  const renderScene = new RenderPass(scene, camera)
  const bloomPass = new UnrealBloomPass(new Vector2(window.innerWidth, window.innerHeight), strength, radius, threshold)

  const bloomComposer = new EffectComposer(renderer)

  bloomComposer.renderToScreen = false
  bloomComposer.addPass(renderScene)
  bloomComposer.addPass(bloomPass)

  const effectCopy = new ShaderPass(CopyShader)
  effectCopy.renderToScreen = true

  const effectFXAA = new ShaderPass(FXAAShader)
  effectFXAA.uniforms.resolution.value.set(1 / window.innerWidth, 1 / window.innerHeight)

  const mixPass = new ShaderPass(
    new ShaderMaterial({
      uniforms: {
        baseTexture: { value: null },
        bloomTexture: { value: bloomComposer.renderTarget2.texture },
      },
      vertexShader: `
    varying vec2 vUv;
    void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
}
    `,
      fragmentShader: `
    uniform sampler2D baseTexture;
    uniform sampler2D bloomTexture;
    varying vec2 vUv;

    void main() {
        gl_FragColor = ( texture2D( baseTexture, vUv ) + vec4( 1.0 ) * texture2D( bloomTexture, vUv ) );
    }
    `,
      defines: {},
    }), 'baseTexture',
  )
  mixPass.needsSwap = true

  const gammaCorrectionShader = new ShaderPass(GammaCorrectionShader)

  composer.addPass(renderScene)
  composer.addPass(mixPass)
  composer.addPass(gammaCorrectionShader)
  composer.addPass(effectCopy)

  const materials = {} as any
  const darkMaterial = new MeshBasicMaterial({ color: 'black' })

  /**
   * 性能消耗太大了
   */
  const cleanBloomBefore = use.useframeAfter(sceneControl, () => {
    const old = scene.background?.clone() ?? null

    scene.traverse((obj: any) => {
      if (!obj.visible)
        return

      if (obj.type === 'SpritePoi') {
        if (obj.material && obj.material.map) {
          obj.material = new SpriteMaterial({ map: obj.material.map, transparent: true, opacity: obj.material.opacity, depthTest: obj.material.depthTest })
          materials[obj.uuid] = obj.material
          obj.material = new SpriteMaterial({ color: 'black' })
        }
      }

      if (scene.background)
        scene.background = new Color('#000')

      if (obj.isMesh && bloomLayer.test(obj.layers) === false) {
        materials[obj.uuid] = obj.material
        obj.material = darkMaterial
      }
    })

    bloomComposer.render()

    scene.traverse((obj: any) => {
      if (!obj.visible)
        return

      if (materials[obj.uuid]) {
        obj.material = materials[obj.uuid]
        delete materials[obj.uuid]
      }
    })

    if (old)
      scene.background = old

    composer.render()
  })

  const bloomSelected = (object3dArray: Object3D[]) => {
    object3dArray.forEach((object3d) => {
      if (object3d.type === 'Mesh') {
        object3d.layers.enable(BLOOM_SCENE)
      }

      else if (object3d.type === 'Group') {
        object3d.traverse((child) => {
          if (child.type === 'Mesh')
            child.layers.enable(BLOOM_SCENE)
        })
      }
    })
  }

  return {
    bloomSelected,
    clean: () => {
      cleanBloomBefore()

      // @ts-ignore
      composer.isNeedRender = true
    },
  }
}
